import type {
  AdmissionsAssistantWorkbookColumn,
  AdmissionsAssistantWorkbookColumnKind,
  AdmissionsAssistantWorkbookSheet,
} from '../components/ai/types';

type SpreadsheetCellValue = string | number | boolean | null;

export type SpreadsheetGeneratedFilterOption = {
  value: string;
  label: string;
  count: number;
};

export type SpreadsheetGeneratedNumericPreset = {
  id: string;
  label: string;
  min?: number;
  max?: number;
};

export type SpreadsheetGeneratedFilterDefinition =
  | {
      kind: 'categorical';
      presentation: 'chips' | 'multi_select';
      columnKey: string;
      header: string;
      options: SpreadsheetGeneratedFilterOption[];
    }
  | {
      kind: 'boolean';
      presentation: 'segmented';
      columnKey: string;
      header: string;
      trueLabel: string;
      falseLabel: string;
      trueCount: number;
      falseCount: number;
    }
  | {
      kind: 'date';
      presentation: 'bucketed_date';
      columnKey: string;
      header: string;
      options: SpreadsheetGeneratedFilterOption[];
      min: string;
      max: string;
    }
  | {
      kind: 'numeric';
      presentation: 'range';
      columnKey: string;
      header: string;
      format: Extract<AdmissionsAssistantWorkbookColumnKind, 'number' | 'currency' | 'percentage'>;
      min: number;
      max: number;
      presets: SpreadsheetGeneratedNumericPreset[];
    };

export type SpreadsheetDateFilterValue = {
  buckets: string[];
  start: string | null;
  end: string | null;
};

export type SpreadsheetNumericFilterValue = {
  min: string;
  max: string;
};

export type SpreadsheetSheetFilterState = {
  categoricalSelections: Record<string, string[]>;
  booleanSelections: Record<string, 'all' | 'true' | 'false'>;
  dateSelections: Record<string, SpreadsheetDateFilterValue>;
  numericSelections: Record<string, SpreadsheetNumericFilterValue>;
};

export type SpreadsheetActiveFilterChip = {
  id: string;
  columnKey: string;
  label: string;
  valueLabel: string;
  clearAction:
    | { kind: 'categorical'; value: string }
    | { kind: 'boolean' }
    | { kind: 'date-bucket'; value: string }
    | { kind: 'date-start' }
    | { kind: 'date-end' }
    | { kind: 'numeric-min' }
    | { kind: 'numeric-max' };
};

export type SpreadsheetVisibleRow = {
  row: Record<string, SpreadsheetCellValue>;
  sourceRowIndex: number;
};

export type SpreadsheetSheetViewResult = {
  rows: SpreadsheetVisibleRow[];
  totalRowCount: number;
  visibleRowCount: number;
  activeFilterChips: SpreadsheetActiveFilterChip[];
};

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US');
const PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const BOOLEAN_TRUE_VALUES = new Set(['true', 'yes', 'y', '1']);
const BOOLEAN_FALSE_VALUES = new Set(['false', 'no', 'n', '0']);
const DATE_VALUE_PATTERN = /^\d{4}-\d{2}(-\d{2})?/;

const normalizeText = (value: unknown): string => (
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
);

const formatCellValue = (value: SpreadsheetCellValue): string => {
  if (value == null) {
    return 'Blank';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value).trim() || 'Blank';
};

const parseBooleanValue = (value: SpreadsheetCellValue): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }
  const normalized = normalizeText(value);
  if (!normalized) {
    return null;
  }
  if (BOOLEAN_TRUE_VALUES.has(normalized)) {
    return true;
  }
  if (BOOLEAN_FALSE_VALUES.has(normalized)) {
    return false;
  }
  return null;
};

const parseNumericValue = (
  value: SpreadsheetCellValue,
  kind: AdmissionsAssistantWorkbookColumnKind,
): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = trimmed
    .replace(/[$,%\s]/g, '')
    .replace(/,/g, '');
  if (!normalized) {
    return null;
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  if (kind === 'percentage' && Math.abs(parsed) > 1) {
    return parsed / 100;
  }
  return parsed;
};

const parseDateValue = (value: SpreadsheetCellValue): Date | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const toMonthBucket = (value: Date): string => value.toISOString().slice(0, 7);

const monthLabel = (bucket: string): string => {
  const [year, month] = bucket.split('-').map((part) => Number(part));
  if (!year || !month) {
    return bucket;
  }
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatNumericValue = (
  value: number,
  kind: Extract<AdmissionsAssistantWorkbookColumnKind, 'number' | 'currency' | 'percentage'>,
): string => {
  if (kind === 'currency') {
    return CURRENCY_FORMATTER.format(value);
  }
  if (kind === 'percentage') {
    return PERCENT_FORMATTER.format(value);
  }
  return NUMBER_FORMATTER.format(value);
};

const quantile = (values: number[], ratio: number): number => {
  if (values.length === 0) {
    return 0;
  }
  const index = Math.min(values.length - 1, Math.max(0, Math.floor((values.length - 1) * ratio)));
  return values[index]!;
};

const buildNumericPresets = (
  values: number[],
  kind: Extract<AdmissionsAssistantWorkbookColumnKind, 'number' | 'currency' | 'percentage'>,
): SpreadsheetGeneratedNumericPreset[] => {
  if (values.length < 2) {
    return [];
  }
  const sorted = [...values].sort((left, right) => left - right);
  const min = sorted[0]!;
  const max = sorted[sorted.length - 1]!;
  if (min === max) {
    return [];
  }
  const median = quantile(sorted, 0.5);
  const upperQuartile = quantile(sorted, 0.75);
  const lowerQuartile = quantile(sorted, 0.25);
  const presets: SpreadsheetGeneratedNumericPreset[] = [];
  if (lowerQuartile > min) {
    presets.push({
      id: 'low',
      label: `Up to ${formatNumericValue(lowerQuartile, kind)}`,
      max: lowerQuartile,
    });
  }
  if (median > min && median < max) {
    presets.push({
      id: 'median_plus',
      label: `${formatNumericValue(median, kind)}+`,
      min: median,
    });
  }
  if (upperQuartile > min && upperQuartile < max) {
    presets.push({
      id: 'top_quartile',
      label: `Top quartile (${formatNumericValue(upperQuartile, kind)}+)`,
      min: upperQuartile,
    });
  }
  return presets;
};

export const createSpreadsheetSheetFilterState = (): SpreadsheetSheetFilterState => ({
  categoricalSelections: {},
  booleanSelections: {},
  dateSelections: {},
  numericSelections: {},
});

const deriveCategoricalFilter = (
  column: AdmissionsAssistantWorkbookColumn,
  rows: Array<Record<string, SpreadsheetCellValue>>,
): SpreadsheetGeneratedFilterDefinition | null => {
  const counts = new Map<string, { label: string; count: number }>();
  for (const row of rows) {
    const label = formatCellValue(row[column.key] ?? null);
    const normalized = normalizeText(label);
    const current = counts.get(normalized) ?? { label, count: 0 };
    current.count += 1;
    counts.set(normalized, current);
  }
  if (counts.size <= 1) {
    return null;
  }
  const options = [...counts.entries()]
    .map(([value, entry]) => ({
      value,
      label: entry.label,
      count: entry.count,
    }))
    .sort((left, right) => (
      right.count - left.count
      || left.label.localeCompare(right.label, undefined, { sensitivity: 'base' })
    ));
  return {
    kind: 'categorical',
    presentation: options.length <= 8 ? 'chips' : 'multi_select',
    columnKey: column.key,
    header: column.header,
    options,
  };
};

const deriveBooleanFilter = (
  column: AdmissionsAssistantWorkbookColumn,
  rows: Array<Record<string, SpreadsheetCellValue>>,
): SpreadsheetGeneratedFilterDefinition | null => {
  let trueCount = 0;
  let falseCount = 0;
  let supportedCount = 0;
  for (const row of rows) {
    const parsed = parseBooleanValue(row[column.key] ?? null);
    if (parsed == null) {
      continue;
    }
    supportedCount += 1;
    if (parsed) {
      trueCount += 1;
    } else {
      falseCount += 1;
    }
  }
  if (supportedCount === 0 || (trueCount === 0 && falseCount === 0) || trueCount === supportedCount || falseCount === supportedCount) {
    return null;
  }
  return {
    kind: 'boolean',
    presentation: 'segmented',
    columnKey: column.key,
    header: column.header,
    trueLabel: 'Yes',
    falseLabel: 'No',
    trueCount,
    falseCount,
  };
};

const deriveDateFilter = (
  column: AdmissionsAssistantWorkbookColumn,
  rows: Array<Record<string, SpreadsheetCellValue>>,
): SpreadsheetGeneratedFilterDefinition | null => {
  const dates = rows
    .map((row) => parseDateValue(row[column.key] ?? null))
    .filter((value): value is Date => Boolean(value));
  if (dates.length < 2) {
    return null;
  }
  const counts = new Map<string, number>();
  for (const value of dates) {
    const bucket = toMonthBucket(value);
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  const options = [...counts.entries()]
    .sort((left, right) => right[0].localeCompare(left[0]))
    .map(([bucket, count]) => ({
      value: bucket,
      label: monthLabel(bucket),
      count,
    }));
  if (options.length <= 1 && dates[0]!.toISOString().slice(0, 10) === dates[dates.length - 1]!.toISOString().slice(0, 10)) {
    return null;
  }
  const sortedDates = [...dates].sort((left, right) => left.getTime() - right.getTime());
  return {
    kind: 'date',
    presentation: 'bucketed_date',
    columnKey: column.key,
    header: column.header,
    options,
    min: sortedDates[0]!.toISOString().slice(0, 10),
    max: sortedDates[sortedDates.length - 1]!.toISOString().slice(0, 10),
  };
};

const deriveNumericFilter = (
  column: AdmissionsAssistantWorkbookColumn,
  rows: Array<Record<string, SpreadsheetCellValue>>,
): SpreadsheetGeneratedFilterDefinition | null => {
  const format = column.kind === 'currency' || column.kind === 'percentage' ? column.kind : 'number';
  const values = rows
    .map((row) => parseNumericValue(row[column.key] ?? null, format))
    .filter((value): value is number => value != null);
  if (values.length < 2) {
    return null;
  }
  const sorted = [...values].sort((left, right) => left - right);
  const min = sorted[0]!;
  const max = sorted[sorted.length - 1]!;
  if (min === max) {
    return null;
  }
  return {
    kind: 'numeric',
    presentation: 'range',
    columnKey: column.key,
    header: column.header,
    format,
    min,
    max,
    presets: buildNumericPresets(sorted, format),
  };
};

const shouldTreatAsDateColumn = (
  column: AdmissionsAssistantWorkbookColumn,
  rows: Array<Record<string, SpreadsheetCellValue>>,
): boolean => {
  if (column.kind === 'date') {
    return true;
  }
  const values = rows
    .map((row) => row[column.key] ?? null)
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  return values.length > 1 && values.every((value) => DATE_VALUE_PATTERN.test(value) && parseDateValue(value) != null);
};

const shouldTreatAsNumericColumn = (
  column: AdmissionsAssistantWorkbookColumn,
  rows: Array<Record<string, SpreadsheetCellValue>>,
): boolean => {
  if (column.kind === 'number' || column.kind === 'currency' || column.kind === 'percentage') {
    return true;
  }
  const values = rows
    .map((row) => row[column.key] ?? null)
    .filter((value) => value != null);
  return values.length > 1 && values.every((value) => parseNumericValue(value, 'number') != null);
};

export const deriveSpreadsheetSheetFilterDefinitions = (
  sheet: AdmissionsAssistantWorkbookSheet,
): SpreadsheetGeneratedFilterDefinition[] => {
  if (!sheet.rows.length) {
    return [];
  }

  return sheet.columns.flatMap((column) => {
    const booleanFilter = deriveBooleanFilter(column, sheet.rows);
    if (booleanFilter) {
      return [booleanFilter];
    }
    if (shouldTreatAsDateColumn(column, sheet.rows)) {
      const dateFilter = deriveDateFilter(column, sheet.rows);
      return dateFilter ? [dateFilter] : [];
    }
    if (shouldTreatAsNumericColumn(column, sheet.rows)) {
      const numericFilter = deriveNumericFilter(column, sheet.rows);
      return numericFilter ? [numericFilter] : [];
    }
    const categoricalFilter = deriveCategoricalFilter(column, sheet.rows);
    return categoricalFilter ? [categoricalFilter] : [];
  });
};

const getDateFilterValue = (
  state: SpreadsheetSheetFilterState,
  columnKey: string,
): SpreadsheetDateFilterValue => (
  state.dateSelections[columnKey] ?? {
    buckets: [],
    start: null,
    end: null,
  }
);

const getNumericFilterValue = (
  state: SpreadsheetSheetFilterState,
  columnKey: string,
): SpreadsheetNumericFilterValue => (
  state.numericSelections[columnKey] ?? {
    min: '',
    max: '',
  }
);

const rowMatchesDefinition = (
  row: Record<string, SpreadsheetCellValue>,
  definition: SpreadsheetGeneratedFilterDefinition,
  state: SpreadsheetSheetFilterState,
): boolean => {
  const rawValue = row[definition.columnKey] ?? null;
  if (definition.kind === 'categorical') {
    const selectedValues = state.categoricalSelections[definition.columnKey] ?? [];
    if (selectedValues.length === 0) {
      return true;
    }
    return selectedValues.includes(normalizeText(formatCellValue(rawValue)));
  }

  if (definition.kind === 'boolean') {
    const selection = state.booleanSelections[definition.columnKey] ?? 'all';
    if (selection === 'all') {
      return true;
    }
    const parsed = parseBooleanValue(rawValue);
    if (parsed == null) {
      return false;
    }
    return selection === 'true' ? parsed : !parsed;
  }

  if (definition.kind === 'date') {
    const selection = getDateFilterValue(state, definition.columnKey);
    const parsed = parseDateValue(rawValue);
    if (!parsed) {
      return selection.buckets.length === 0 && !selection.start && !selection.end;
    }
    const bucket = toMonthBucket(parsed);
    if (selection.buckets.length > 0 && !selection.buckets.includes(bucket)) {
      return false;
    }
    const iso = parsed.toISOString().slice(0, 10);
    if (selection.start && iso < selection.start) {
      return false;
    }
    if (selection.end && iso > selection.end) {
      return false;
    }
    return true;
  }

  const selection = getNumericFilterValue(state, definition.columnKey);
  const parsed = parseNumericValue(rawValue, definition.format);
  if (!selection.min && !selection.max) {
    return true;
  }
  if (parsed == null) {
    return false;
  }
  const min = selection.min.length > 0 ? Number(selection.min) : null;
  const max = selection.max.length > 0 ? Number(selection.max) : null;
  if (min != null && Number.isFinite(min) && parsed < min) {
    return false;
  }
  if (max != null && Number.isFinite(max) && parsed > max) {
    return false;
  }
  return true;
};

export const getSpreadsheetActiveFilterChips = (
  definitions: SpreadsheetGeneratedFilterDefinition[],
  state: SpreadsheetSheetFilterState,
): SpreadsheetActiveFilterChip[] => {
  const chips: SpreadsheetActiveFilterChip[] = [];

  for (const definition of definitions) {
    if (definition.kind === 'categorical') {
      const selections = state.categoricalSelections[definition.columnKey] ?? [];
      for (const value of selections) {
        const option = definition.options.find((candidate) => candidate.value === value);
        chips.push({
          id: `${definition.columnKey}:categorical:${value}`,
          columnKey: definition.columnKey,
          label: definition.header,
          valueLabel: option?.label ?? value,
          clearAction: { kind: 'categorical', value },
        });
      }
      continue;
    }

    if (definition.kind === 'boolean') {
      const selection = state.booleanSelections[definition.columnKey] ?? 'all';
      if (selection !== 'all') {
        chips.push({
          id: `${definition.columnKey}:boolean`,
          columnKey: definition.columnKey,
          label: definition.header,
          valueLabel: selection === 'true' ? definition.trueLabel : definition.falseLabel,
          clearAction: { kind: 'boolean' },
        });
      }
      continue;
    }

    if (definition.kind === 'date') {
      const selection = getDateFilterValue(state, definition.columnKey);
      for (const bucket of selection.buckets) {
        const option = definition.options.find((candidate) => candidate.value === bucket);
        chips.push({
          id: `${definition.columnKey}:date-bucket:${bucket}`,
          columnKey: definition.columnKey,
          label: definition.header,
          valueLabel: option?.label ?? bucket,
          clearAction: { kind: 'date-bucket', value: bucket },
        });
      }
      if (selection.start) {
        chips.push({
          id: `${definition.columnKey}:date-start`,
          columnKey: definition.columnKey,
          label: definition.header,
          valueLabel: `From ${selection.start}`,
          clearAction: { kind: 'date-start' },
        });
      }
      if (selection.end) {
        chips.push({
          id: `${definition.columnKey}:date-end`,
          columnKey: definition.columnKey,
          label: definition.header,
          valueLabel: `Until ${selection.end}`,
          clearAction: { kind: 'date-end' },
        });
      }
      continue;
    }

    const selection = getNumericFilterValue(state, definition.columnKey);
    if (selection.min) {
      chips.push({
        id: `${definition.columnKey}:numeric-min`,
        columnKey: definition.columnKey,
        label: definition.header,
        valueLabel: `Min ${formatNumericValue(Number(selection.min), definition.format)}`,
        clearAction: { kind: 'numeric-min' },
      });
    }
    if (selection.max) {
      chips.push({
        id: `${definition.columnKey}:numeric-max`,
        columnKey: definition.columnKey,
        label: definition.header,
        valueLabel: `Max ${formatNumericValue(Number(selection.max), definition.format)}`,
        clearAction: { kind: 'numeric-max' },
      });
    }
  }

  return chips;
};

export const applySpreadsheetSheetViewFilters = (input: {
  sheet: AdmissionsAssistantWorkbookSheet;
  definitions: SpreadsheetGeneratedFilterDefinition[];
  state: SpreadsheetSheetFilterState;
}): SpreadsheetSheetViewResult => {
  const rows = input.sheet.rows
    .map((row, sourceRowIndex) => ({ row, sourceRowIndex }))
    .filter(({ row }) => input.definitions.every((definition) => rowMatchesDefinition(row, definition, input.state)));
  return {
    rows,
    totalRowCount: input.sheet.rows.length,
    visibleRowCount: rows.length,
    activeFilterChips: getSpreadsheetActiveFilterChips(input.definitions, input.state),
  };
};
